"use client"

import {
  gachaEvmBuybackAvailableSchema,
  gachaEvmBuybackInputSchema,
} from "@anaconda/core/gacha/evm-buyback"
import {
  type GachaEnvironment,
  getGachaEvmChains,
} from "@anaconda/core/gacha/evm-chains"
import { wrap } from "@reatom/core"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { type GachaApiOptions, getGachaApiUrl } from "./gacha-api"
import { getGachaBuybackDeadlineMs } from "./portfolio/gacha-buyback"
import type { GachaEvmCardIdentity } from "./portfolio/gacha-evm-portfolio"

export type GachaEvmBuybackAvailabilityOptions = {
  environment: GachaEnvironment | null
  identity: GachaEvmCardIdentity
  walletAddress: string
  apiOptions: GachaApiOptions
  enabled?: boolean
}

export function getGachaEvmBuybackAvailabilityKey(
  options: GachaEvmBuybackAvailabilityOptions,
) {
  return [
    "gacha-evm-buyback-available",
    options.apiOptions.bffBaseUrl,
    options.apiOptions.collectorCryptGachaBaseUrl,
    options.environment,
    options.identity.chainId,
    options.identity.contract.toLowerCase(),
    options.identity.tokenId,
    options.walletAddress.toLowerCase(),
  ]
}

// GET only, through the BFF's authenticated CollectorCrypt client. No wallet
// provider, quote issuance, approval, or signing is involved in this read.
export async function fetchGachaEvmBuybackAvailability(
  options: GachaEvmBuybackAvailabilityOptions,
  signal?: AbortSignal,
) {
  const input = gachaEvmBuybackInputSchema.parse({
    playerAddress: options.walletAddress,
    evmContract: options.identity.contract,
    evmTokenId: options.identity.tokenId,
    chainId: options.identity.chainId,
  })
  const chain = options.environment
    ? getGachaEvmChains(options.environment).find(
        (candidate) => candidate.chainId === input.chainId,
      )
    : undefined
  if (
    !chain ||
    chain.cardContract.toLowerCase() !== input.evmContract.toLowerCase()
  )
    throw new Error("Buyback environment or card unavailable")
  const startedAt = Date.now()
  const response = await wrap(
    fetch(
      getGachaApiUrl(
        `/evm/buyback/available?${new URLSearchParams({
          wallet: input.playerAddress,
          tokenId: input.evmTokenId,
          contract: input.evmContract,
          chainId: String(input.chainId),
        })}`,
        options.apiOptions,
      ),
      {
        method: "GET",
        cache: "no-store",
        signal: signal
          ? AbortSignal.any([signal, AbortSignal.timeout(15_000)])
          : AbortSignal.timeout(15_000),
      },
    ),
  )
  if (!response.ok || response.status === 202)
    throw new Error("Buyback availability unavailable; retry later.")
  const data = gachaEvmBuybackAvailableSchema.parse(await wrap(response.json()))
  if (data.chainId !== input.chainId)
    throw new Error("Buyback availability chain mismatch")
  const openedDeadline = data.openedAt
    ? getGachaBuybackDeadlineMs(Date.parse(data.openedAt))
    : undefined
  const upstreamDeadline =
    data.timeRemaining === undefined
      ? undefined
      : startedAt + data.timeRemaining * 1000
  return {
    available: data.available,
    eligibilityUntil:
      openedDeadline === undefined
        ? upstreamDeadline
        : upstreamDeadline === undefined
          ? openedDeadline
          : Math.min(openedDeadline, upstreamDeadline),
    // amountBase intentionally not exposed as a payout: this endpoint does not
    // identify the original payment token or its decimals. A verified quote does.
  }
}

/** Call from a reatomComponent. Expiry is milliseconds, never NFT metadata time. */
export function useGachaEvmBuybackAvailability(
  options: GachaEvmBuybackAvailabilityOptions,
) {
  const enabled =
    options.enabled !== false &&
    Boolean(options.environment && options.walletAddress)
  const query = useQuery({
    queryKey: getGachaEvmBuybackAvailabilityKey(options),
    queryFn: wrap(({ signal }: { signal: AbortSignal }) =>
      fetchGachaEvmBuybackAvailability(options, signal),
    ),
    enabled,
    retry: 1,
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })
  const [now, setNow] = useState(Date.now)
  const eligibilityUntil =
    enabled && !query.isError && !query.isPlaceholderData
      ? query.data?.eligibilityUntil
      : undefined
  // biome-ignore lint/correctness/useExhaustiveDependencies: wrap preserves context; expiry controls the timer lifetime.
  useEffect(
    wrap(() => {
      if (eligibilityUntil === undefined) return
      setNow(Date.now())
      const timer = window.setInterval(
        wrap(() => setNow(Date.now())),
        1000,
      )
      return () => window.clearInterval(timer)
    }),
    [eligibilityUntil],
  )
  const eligible =
    !enabled || query.isError || query.isPlaceholderData || !query.data
      ? undefined
      : !query.data.available
        ? false
        : eligibilityUntil === undefined
          ? undefined
          : eligibilityUntil > Math.max(now, Date.now())
  return { eligible, eligibilityUntil, query }
}
